import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { LiveNursesGridItem } from 'src/app/recruiting/candidates/live-nurses-new/grid/models/live-nurses-grid-item.model';
import { combine } from 'src/app/shared/utilities/route';
import { PagingToken, FetchArgs } from 'src/app/shared/models/core';
import { NoteItem } from 'src/app/recruiting/candidates/live-nurses-new/components/notes/models/dto/note-item';
import { JobItem } from 'src/app/recruiting/candidates/live-nurses-new/components/jobs/models/dto/job-item';
import { PostedJobDto } from 'src/app/recruiting/candidates/live-nurses-new/components/job-profile/models/dto/posted-job';
import { Lookup } from 'src/app/shared/models/lookup.model';
import { Observable } from 'rxjs';
import { SubmittalJobMatchCalculation } from '../submittal/models/submittal-job-match-calculation.model';

@Injectable({
    providedIn: 'root'
})
export class LiveNursesService {
    private coreApiPath: string;

    constructor(
        @Inject(APP_CONFIG) settings: Settings,
        private readonly http: HttpClient
    ) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'ayanova/');
    }

    search(recruiterId: number, args: FetchArgs): Observable<PagingToken<LiveNursesGridItem[]>> {
        const url = combine(this.coreApiPath, `candidates/live-nurses/${recruiterId}`);
        return this.postPaged(url, args);
    }

    getPostedJob(jobId: number): Observable<PostedJobDto> {
        const url = combine(this.coreApiPath, `candidates/live-nurses/posted-job/${jobId}`);
        return this.http.get<PostedJobDto>(url);
    }

    getSubmittalNotes(submittalId: number): Observable<NoteItem[]> {
        const url = combine(this.coreApiPath, `candidates/live-nurses/submittals/${submittalId}/notes`);
        return this.http.get<NoteItem[]>(url);
    }

    getJobOptions(submittalIds: number[]): Observable<JobItem[]> {
        const url = combine(this.coreApiPath, `candidates/live-nurses/submittals/jobs`);
        return this.http.post<JobItem[]>(url, submittalIds);
    }

    deleteJob(
        jobId: number,
        submittalId: number,
        submittalStatusId: number | null = null,
        submittalSubStatusId: number | null = null
    ): Observable<void> {
        let url = combine(this.coreApiPath, `candidates/live-nurses/submittals/${submittalId}/jobs/${jobId}`);
        if (submittalStatusId) {
            url += `?submittalStatusId=${submittalStatusId}`;

            if (submittalSubStatusId) {
                url += `&submittalSubStatusId=${submittalSubStatusId}`;
            }
        }

        return this.http.delete<void>(url);
    }

    deleteSubmittal(
        submittalId: number,
        reason: string,
        submittalStatusId: number | null = null,
        submittalSubStatusId: number | null = null
    ): Observable<void> {
        let url = combine(this.coreApiPath, `candidates/live-nurses/submittals/${submittalId}?reason=${reason}`);
        if (submittalStatusId) {
            url += `&submittalStatusId=${submittalStatusId}`;

            if (submittalSubStatusId) {
                url += `&submittalSubStatusId=${submittalSubStatusId}`;
            }
        }

        return this.http.delete<void>(url);
    }

    addJob(job: JobItem): Observable<JobItem> {
        const url = combine(this.coreApiPath, `candidates/live-nurses/submittals/add/job`);
        return this.http.post<JobItem>(url, job);
    }

    addNote(note: NoteItem): Observable<NoteItem> {
        const url = combine(this.coreApiPath, `candidates/live-nurses/submittals/add/note`);
        return this.http.post<NoteItem>(url, note);
    }

    getRecruiters(args: FetchArgs): Observable<PagingToken<Lookup[]>> {
        const url = combine(this.coreApiPath, `candidates/live-nurses/recruiters`);
        return this.postPaged<Lookup>(url, args);
    }

    protected postPaged<T>(url: string, fetchArgs: FetchArgs): Observable<PagingToken<T[]>> {
        return this.http.post<PagingToken<T[]>>(url, fetchArgs, { observe: 'response' }).pipe(
            map((response) => {
                return {
                    total: response.body.total,
                    data: response.body.data
                };
            })
        );
    }

    getRecruitersViewOthersListing(): Observable<Lookup[]> {
        const url = combine(this.coreApiPath, `candidates/live-nurses/recruiters/view-others-listing`);
        return this.http.get<Lookup[]>(url);
    }

    getRecruiterCalculations(recruiterId: number): Observable<SubmittalJobMatchCalculation[]> {
        const getUri = combine(this.coreApiPath, `submittals/submittalJobQualifications/calculations/${recruiterId}`);
        return this.http.get<SubmittalJobMatchCalculation[]>(getUri);
    }

    updateKeepRequested(submittalId: number, keep: boolean) {
        const url = combine(
            this.coreApiPath,
            `candidates/live-nurses/submittals-pending-removal/${submittalId}/keep-request`
        );
        return this.http.put(url, keep);
    }
}
